// v1.0.0 - 2026-01-27 - SheetJS utility functions

import * as XLSX from 'xlsx'

/**
 * Read workbook from ArrayBuffer
 */
export function readWorkbook(buffer: ArrayBuffer): XLSX.WorkBook {
  return XLSX.read(buffer, { type: 'array', cellDates: true })
}

/**
 * Read workbook from File
 */
export async function readWorkbookFromFile(file: File): Promise<XLSX.WorkBook> {
  const buffer = await file.arrayBuffer()
  return readWorkbook(buffer)
}

/**
 * Get sheet names from workbook
 */
export function getSheetNames(workbook: XLSX.WorkBook): string[] {
  return workbook.SheetNames
}

/**
 * Get sheet by name or index
 */
export function getSheet(
  workbook: XLSX.WorkBook,
  nameOrIndex: string | number
): XLSX.WorkSheet | null {
  if (typeof nameOrIndex === 'number') {
    const name = workbook.SheetNames[nameOrIndex]
    return name ? workbook.Sheets[name] : null
  }
  return workbook.Sheets[nameOrIndex] || null
}

/**
 * Convert sheet to JSON array
 */
export function sheetToJson<T = Record<string, unknown>>(
  sheet: XLSX.WorkSheet,
  options?: XLSX.Sheet2JSONOpts
): T[] {
  return XLSX.utils.sheet_to_json<T>(sheet, {
    defval: null,
    raw: false,
    ...options,
  })
}

/**
 * Get cell value from sheet
 */
export function getCellValue(
  sheet: XLSX.WorkSheet,
  col: string,
  row: number
): string | number | null {
  const cell = sheet[`${col}${row}`]
  if (!cell) return null
  return cell.v
}

/**
 * Find column index by possible header names
 */
export function findColumnByHeaders(
  row: Record<string, unknown>,
  possibleHeaders: readonly string[]
): string | null {
  for (const header of possibleHeaders) {
    if (header in row) {
      return header
    }
  }
  return null
}

/**
 * Get row value with fallback column names
 */
export function getRowValue<T = unknown>(
  row: Record<string, unknown>,
  possibleKeys: readonly string[],
  defaultValue: T | null = null
): T | null {
  for (const key of possibleKeys) {
    if (key in row && row[key] != null && row[key] !== '') {
      return row[key] as T
    }
  }
  return defaultValue
}

/**
 * Parse numeric value from cell
 */
export function parseNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null
  }

  if (typeof value === 'number') {
    return isNaN(value) ? null : value
  }

  const str = String(value)
    .trim()
    .replace(/\s/g, '')
    .replace(/,/g, '.') // Italian decimal separator
    .replace(/[^\d.\-]/g, '')

  const num = parseFloat(str)
  return isNaN(num) ? null : num
}

/**
 * Parse integer value from cell
 */
export function parseInt(value: unknown): number | null {
  const num = parseNumber(value)
  return num !== null ? Math.round(num) : null
}

/**
 * Parse date from various formats
 */
export function parseDate(value: unknown): Date | null {
  if (!value) return null

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value
  }

  const str = String(value).trim()

  // Try DD/MM/YYYY format (GSE standard)
  const ddmmyyyy = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (ddmmyyyy) {
    const [, day, month, year] = ddmmyyyy
    const date = new Date(Number(year), Number(month) - 1, Number(day))
    return isNaN(date.getTime()) ? null : date
  }

  // Try YYYY-MM-DD format (ISO)
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (iso) {
    const [, year, month, day] = iso
    const date = new Date(Number(year), Number(month) - 1, Number(day))
    return isNaN(date.getTime()) ? null : date
  }

  // Try YYYYMMDD format
  const compact = str.match(/^(\d{4})(\d{2})(\d{2})$/)
  if (compact) {
    const [, year, month, day] = compact
    const date = new Date(Number(year), Number(month) - 1, Number(day))
    return isNaN(date.getTime()) ? null : date
  }

  // Fallback to native Date parsing
  const date = new Date(str)
  return isNaN(date.getTime()) ? null : date
}

/**
 * Format date to ISO string (YYYY-MM-DD)
 */
export function formatDateISO(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Parse period from various formats (202501 → 2025-01)
 */
export function parsePeriod(value: unknown): string | null {
  if (!value) return null

  const str = String(value).trim()

  // Already in YYYY-MM format
  if (/^\d{4}-\d{2}$/.test(str)) {
    return str
  }

  // YYYYMM format
  if (/^\d{6}$/.test(str)) {
    return `${str.slice(0, 4)}-${str.slice(4, 6)}`
  }

  // MM/YYYY format
  const mmyyyy = str.match(/^(\d{1,2})\/(\d{4})$/)
  if (mmyyyy) {
    const [, month, year] = mmyyyy
    return `${year}-${month.padStart(2, '0')}`
  }

  return null
}

/**
 * Detect sheet type from name or content
 */
export function detectSheetType(
  sheetName: string,
  firstRow?: Record<string, unknown>
): 'CORRISPETTIVI' | 'RIEPILOGO' | 'CURVA' | 'UNKNOWN' {
  const name = sheetName.toLowerCase()

  if (name.includes('corrispettivi') || name.includes('tip')) {
    return 'CORRISPETTIVI'
  }

  if (name.includes('riepilogo') || name.includes('totali') || name.includes('summary')) {
    return 'RIEPILOGO'
  }

  if (name.includes('curva') || name.includes('orari') || name.includes('hour')) {
    return 'CURVA'
  }

  // Try to detect from column headers
  if (firstRow) {
    const keys = Object.keys(firstRow).map((k) => k.toLowerCase())

    if (keys.some((k) => k.includes('tip') || k.includes('cuafa'))) {
      return 'CORRISPETTIVI'
    }

    if (keys.some((k) => k.includes('ora') || k.includes('hour'))) {
      return 'CURVA'
    }

    if (keys.some((k) => k.includes('pod') && k.includes('totale'))) {
      return 'RIEPILOGO'
    }
  }

  return 'UNKNOWN'
}
